/**
 * Service/Condition Page Component — Restore.Dental
 * 
 * Answers specific patient search intent with:
 * - Question-format H1 (voice-search optimized)
 * - TL;DR paragraph (Speakable-flagged)
 * - Treatment approach, FAQs, aftercare
 * - MedicalCondition + MedicalProcedure + FAQPage + Speakable JSON-LD
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, CheckCircle2, Clock, Award, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import EnquireButton from './EnquireButton';
import PageHeader from './PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { getServiceBySlug } from '../data/services';
import { PUBLIC_SITE_URL } from '../data/seo';
import SiteFooter from './SiteFooter';
import Breadcrumbs from './Breadcrumbs';
import { PRACTICE } from '../data/practice';
import { 
  getMedicalConditionLD,
  getMedicalProcedureLD,
  getFAQPageLD,
  getSpeakableLD,
  getBreadcrumbLD
} from '../data/structuredData';

const ServicePage = () => {
  const location = useLocation();
  
  // Extract slug from path: /services/dental-implants → dental-implants
  const slug = location.pathname.replace('/services/', '');
  const service = getServiceBySlug(slug);

  // Handle 404 if service not found
  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-rabbit mb-4">Service Not Found</h1>
          <Link to="/" className="text-sky-gray hover:underline">
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* SEO Meta Tags & Structured Data */}
      <SEO
        title={service.metaTitle}
        description={service.metaDescription}
        path={`/services/${service.slug}`}
        keywords={service.keywords.split(', ')}
        structuredData={[
          getMedicalConditionLD({
            name: service.questionH1,
            description: service.tldr,
            relevantSpecialty: 'Dentistry'
          }),
          getMedicalProcedureLD({
            name: service.questionH1,
            description: service.tldr,
            preparation: service.treatmentApproach.steps[0].description,
            followup: service.aftercare.note
          }),
          getFAQPageLD(service.faqs),
          getSpeakableLD([service.tldr.substring(0, 150)]),
          getBreadcrumbLD([
            { name: 'Home', url: `${PUBLIC_SITE_URL}/` },
            { name: 'Treatments', url: `${PUBLIC_SITE_URL}/pricing` },
            { name: service.questionH1, url: `${PUBLIC_SITE_URL}/services/${service.slug}` },
          ]),
        ]}
      />

      <div className="min-h-screen bg-white">
        {/* Header */}
        <PageHeader />

        {/* Hero Section */}
        <section className="pt-28 pb-16 px-4 bg-gradient-to-br from-sky-gray/5 to-white">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="flex justify-center mb-6">
              <Breadcrumbs items={[{ name: 'Home', to: '/' }, { name: 'Treatments', to: '/pricing' }, { name: service.shortName || service.questionH1 }]} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-rabbit mb-6 leading-tight">
              {service.questionH1}
            </h1>

            {/* TL;DR - Speakable direct answer */}
            <div className="service-tldr direct-answer bg-white p-6 md:p-8 rounded-2xl border-2 border-sky-gray/20 shadow-lg mb-8 text-left" data-testid="direct-answer">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#006A71] mb-2">Quick answer</p>
              <p className="text-lg text-chocolate-malt leading-relaxed">
                {service.tldr}
              </p>
              <p className="text-sm text-chocolate-malt/80 mt-4">
                Reviewed by <Link to="/about-dr-kartik-patel" className="underline underline-offset-4 text-[#006A71]">Dr Kartik Patel BDS</Link>, GDC {PRACTICE.gdcNumber} · Restore.Dental, {PRACTICE.addressOneLine} · <Link to="/pricing" className="underline underline-offset-4 text-[#006A71]">See prices</Link>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PRACTICE.phoneTel}>
                <Button size="lg" className="w-full sm:w-auto bg-sky-gray hover:bg-burnt-coral">
                  <Phone className="w-5 h-5 mr-2" />
                  Book Consultation
                </Button>
              </a>
              <EnquireButton slug={slug} about={service.shortName || service.questionH1} className="w-full sm:w-auto" testId="hero-enquire-btn" />
              <Link to="/">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  View All Services
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Deeper hub for this treatment, where one exists */}
        {service.hubLink && (
          <section className="border-y border-[#006A71]/15 bg-[#006A71]/[0.05] px-4 py-6">
            <div className="container mx-auto flex max-w-4xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-base text-chocolate-malt">{service.hubLink.blurb}</p>
              <Link
                to={service.hubLink.to}
                className="flex-shrink-0 text-base font-semibold text-[#006A71] underline underline-offset-4 hover:text-[#01A9B4]"
                data-testid="service-hub-link"
              >
                {service.hubLink.label} →
              </Link>
            </div>
          </section>
        )}

        {/* Who It's For */}
        <section className="py-16 px-4 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-rabbit mb-8 text-center">
              Who This Treatment Is For
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {service.whoItsFor.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-sky-gray/5 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-sky-gray flex-shrink-0 mt-1" />
                  <p className="text-chocolate-malt">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Treatment Approach */}
        <section className="py-16 px-4 bg-gradient-to-br from-white to-sky-gray/5">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl font-bold text-rabbit mb-12 text-center">
              {service.treatmentApproach.title}
            </h2>
            <div className="space-y-6">
              {service.treatmentApproach.steps.map((step, index) => (
                <Card key={index} className="border-l-4 border-sky-gray">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-sky-gray text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                        {step.number}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-rabbit mb-2">{step.title}</h3>
                        <p className="text-chocolate-malt">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-rabbit mb-12 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {service.faqs.map((faq, index) => (
                <Card key={index} className="border border-sky-gray/20 hover:border-sky-gray transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-rabbit mb-3">{faq.question}</h3>
                    <p className="text-chocolate-malt leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Aftercare Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-sky-gray/5 to-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-rabbit mb-8 text-center">
              {service.aftercare.title}
            </h2>
            <Card className="border-2 border-sky-gray/20">
              <CardContent className="p-8">
                <ul className="space-y-3 mb-6">
                  {service.aftercare.instructions.map((instruction, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-sky-gray flex-shrink-0 mt-0.5" />
                      <span className="text-chocolate-malt">{instruction}</span>
                    </li>
                  ))}
                </ul>
                <div className="p-4 bg-amberlight/10 rounded-lg border-l-4 border-amberlight">
                  <p className="text-sm text-chocolate-malt">
                    <strong>Note:</strong> {service.aftercare.note}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Related Services */}
        {service.relatedServices && service.relatedServices.length > 0 && (
          <section className="py-16 px-4 bg-white">
            <div className="container mx-auto max-w-4xl">
              <h2 className="text-2xl font-bold text-rabbit mb-6 text-center">
                Related Treatments
              </h2>
              <div className="flex flex-wrap gap-4 justify-center">
                {service.relatedServices.map((relatedSlug, index) => (
                  <Link key={index} to={`/services/${relatedSlug}`}>
                    <Button variant="outline" className="gap-2">
                      {relatedSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="py-16 px-4 bg-gradient-to-br from-sky-gray to-burnt-coral text-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Book a consultation with Dr. Kartik Patel to discuss your treatment options
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PRACTICE.phoneTel}>
                <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                  <Phone className="w-5 h-5 mr-2" />
                  Call 07787 814 064
                </Button>
              </a>
              <EnquireButton slug={slug} about={service.shortName || service.questionH1} className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border-white/30" testId="cta-enquire-btn" />
              <Link to="/">
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border-white/30">
                  View All Services
                </Button>
              </Link>
            </div>
            
            <div className="mt-8 flex flex-wrap gap-6 justify-center text-sm opacity-90">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                <span>4.9★ Google Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>Same-Day Appointments</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                <span>0% Interest-Free Finance</span>
              </div>
            </div>
          </div>
        </section>

        <SiteFooter />
      </div>
    </>
  );
};

export default ServicePage;
